'use client';

import {
  ReactNode,
  RefObject,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { EventParamsType, EventValueType } from '@/analytics';

export type JumpLinkType = {
  id: string;
  navLabel: {
    full: string;
    short?: string;
  };
  destinationRef: RefObject<HTMLDivElement>;
  topOffset?: number;
  tracking?: {
    trackingEventName: EventValueType;
    trackingContext?: EventParamsType;
  };
};

interface JumpLinksContextProps {
  jumpLinks: JumpLinkType[];
  addJumpLink: (link: JumpLinkType) => void;
}

export const JumpLinksContext = createContext<
  JumpLinksContextProps | undefined
>(undefined);

export function JumpLinksProvider({ children }: { children?: ReactNode }) {
  const [links, setLinks] = useState<JumpLinkType[]>([]);

  const addJumpLink = useCallback((link: JumpLinkType) => {
    setLinks((prevLinks: JumpLinkType[]) => {
      if (!prevLinks.find((prevLink) => prevLink.id === link.id)) {
        return [...prevLinks, link];
      }
      return prevLinks;
    });
  }, []);

  const value: JumpLinksContextProps = {
    jumpLinks: links,
    addJumpLink,
  };

  return (
    <JumpLinksContext.Provider value={value}>
      {children}
    </JumpLinksContext.Provider>
  );
}

export function useJumpLinks() {
  const context = useContext(JumpLinksContext);

  if (context === undefined) {
    throw new Error('useJumpLinks must be used within a JumpLinksProvider');
  }

  return context;
}
